import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface SiteWithKPIs {
  id: string;
  name: string;
  location: string;
  status: string;
  start_date: string;
  end_date: string;
  site_manager_id: string | null;
  budget_initial: number | null;
  observations: string | null;
  company_id: string;
  short_code: string | null;

  // Chef et conducteur
  chef_name: string | null;
  supervisors: Array<{ name: string }>;

  // KPIs
  volume_prevu: number;
  volume_livre: number;
  avancement_percent: number;
  total_incidents: number;
  total_deliveries: number;
  taux_incidents: number;

  // État calculé
  health_status: 'ok' | 'warning' | 'problem';
  health_label: string;
  health_icon: string;
}

function calculateHealthStatus(
  incidentRate: number,
  progress: number,
  volumeDelivered: number,
  volumePlanned: number,
  startDate: string,
  endDate: string
): { status: 'ok' | 'warning' | 'problem'; label: string; icon: string } {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  const totalDuration = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();
  const timeProgress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

  if (timeProgress < 5 && progress === 0) {
    if (incidentRate > 15) {
      return { status: 'problem', label: 'Problème', icon: '🔴' };
    }
    if (incidentRate > 5) {
      return { status: 'warning', label: 'À surveiller', icon: '⚠️' };
    }
    return { status: 'ok', label: 'OK', icon: '✔' };
  }

  const theoreticalProgress = timeProgress;
  const planningDeviation = Math.abs(progress - theoreticalProgress);

  if (incidentRate > 15 || planningDeviation > 20) {
    return { status: 'problem', label: 'Problème', icon: '🔴' };
  }

  if (incidentRate > 5 || planningDeviation > 10) {
    return { status: 'warning', label: 'À surveiller', icon: '⚠️' };
  }

  return { status: 'ok', label: 'OK', icon: '✔' };
}

async function fetchSitesWithKPIs(
  userId: string,
  role: string | undefined
): Promise<SiteWithKPIs[]> {
  // Get company_id and assigned sites in parallel
  // For site_manager and project_supervisor, use site_assignments
  const [profileResult, assignmentsResult] = await Promise.all([
    supabase
      .from('profiles')
      .select('company_id')
      .eq('user_id', userId)
      .single(),
    role !== 'company_director'
      ? supabase.from('site_assignments').select('site_id').eq('user_id', userId)
      : Promise.resolve({ data: null })
  ]);

  if (!profileResult.data) {
    throw new Error('Profile not found');
  }

  const companyId = profileResult.data.company_id;
  const assignedSiteIds = assignmentsResult.data?.map(a => a.site_id) || null;
  
  // If site_manager or project_supervisor with no assigned sites, return empty list
  if (role !== 'company_director' && assignedSiteIds?.length === 0) {
    return [];
  }

  // One single query via optimized view
  let query = supabase
    .from('sites_with_kpis_view')
    .select('*')
    .eq('company_id', companyId);
  
  // Filter by assigned sites for non-directors
  if (role !== 'company_director' && assignedSiteIds) {
    query = query.in('id', assignedSiteIds);
  }

  // site_manager only sees in_progress sites
  if (role === 'site_manager') {
    query = query.eq('status', 'in_progress');
  }

  const { data: sitesData, error: sitesError } = await query
    .order('name')
    .limit(50);

  if (sitesError) throw sitesError;

  // Fetch supervisors in a single grouped query
  const siteIds = (sitesData || []).map(s => s.id);
  
  let supervisorsMap = new Map<string, Array<{ name: string }>>();
  
  if (siteIds.length > 0) {
    const { data: supervisorAssignments } = await supabase
      .from('site_assignments')
      .select('site_id, user_id')
      .in('site_id', siteIds)
      .eq('role', 'project_supervisor');
    
    if (supervisorAssignments && supervisorAssignments.length > 0) {
      const supervisorUserIds = [...new Set(supervisorAssignments.map(a => a.user_id))];
      
      const { data: supervisorProfiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', supervisorUserIds);
      
      const profilesMap = new Map(
        (supervisorProfiles || []).map(p => [p.user_id, `${p.first_name} ${p.last_name}`])
      );
      
      supervisorAssignments.forEach(assignment => {
        const name = profilesMap.get(assignment.user_id) || 'N/A';
        if (!supervisorsMap.has(assignment.site_id)) {
          supervisorsMap.set(assignment.site_id, []);
        }
        supervisorsMap.get(assignment.site_id)!.push({ name });
      });
    }
  }

  // Import the view type from Supabase
  type SiteViewRow = {
    id: string | null;
    name: string | null;
    location: string | null;
    status: string | null;
    start_date: string | null;
    end_date: string | null;
    site_manager_id: string | null;
    budget_initial: number | null;
    observations: string | null;
    company_id: string | null;
    short_code: string | null;
    chef_name: string | null;
    volume_prevu: number | null;
    volume_livre: number | null;
    avancement_percent: number | null;
    total_incidents: number | null;
    total_deliveries: number | null;
    taux_incidents: number | null;
  };

  // Transform view data to SiteWithKPIs
  return (sitesData || []).map((site: SiteViewRow) => {
    const health = calculateHealthStatus(
      Number(site.taux_incidents) || 0,
      Number(site.avancement_percent) || 0,
      Number(site.volume_livre) || 0,
      Number(site.volume_prevu) || 0,
      site.start_date || '',
      site.end_date || ''
    );

    return {
      id: site.id || '',
      name: site.name || '',
      location: site.location || '',
      status: site.status || '',
      start_date: site.start_date || '',
      end_date: site.end_date || '',
      site_manager_id: site.site_manager_id,
      budget_initial: site.budget_initial,
      observations: site.observations,
      company_id: site.company_id || '',
      short_code: site.short_code || null,
      chef_name: site.chef_name || null,
      supervisors: supervisorsMap.get(site.id || '') || [],
      volume_prevu: Number(site.volume_prevu) || 0,
      volume_livre: Number(site.volume_livre) || 0,
      avancement_percent: Math.min(100, Number(site.avancement_percent) || 0),
      total_incidents: Number(site.total_incidents) || 0,
      total_deliveries: Number(site.total_deliveries) || 0,
      taux_incidents: Number(site.taux_incidents) || 0,
      health_status: health.status,
      health_label: health.label,
      health_icon: health.icon,
    };
  });
}

export const useSitesWithKPIs = () => {
  const { user } = useAuth();
  
  const { data: sites = [], isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ['sites-with-kpis', user?.id, user?.role],
    queryFn: () => fetchSitesWithKPIs(user!.id, user?.role),
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000,        // 2 minutes
    gcTime: 15 * 60 * 1000,          // 15 min
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
  });

  return {
    sites,
    loading: isLoading,
    isFetching,
    error: error?.message || null,
    refetch,
  };
};
